import React, { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { PAGE_SLAB_MAX_LIMIT } from "../../../../../common/Constant";
import CurrencyInputWithType from "../../../../../common/CurrencyInputWithType";
import CurrencyInputCompact from "./CurrencyInputCompact";

interface ThresholdRow {
  min: string;
  max: string;
  baseRate: string;
  clientRate: string;
  currency?: "USD" | "INR";
  disabled?: boolean;
  isEditing?: boolean;
  originalData?: {
    min: string;
    max: string;
    baseRate: string;
    clientRate: string;
    currency?: "USD" | "INR";
  };
}

interface ThresholdPayload {
  isInputPage: 0 | 1;
  min: number;
  max: number;
  baseRate: number;
  clientRate: number;
  currency?: "USD" | "INR";
  comments?: string;
}

interface Props {
  resetKey?: number;
  isInputPages: "Input" | "Output";
  value?: ThresholdPayload[];
  canAdd?: boolean;
  onChange: (data: ThresholdPayload[]) => void;
  onValidationError: (errors: string[]) => void;
  setSelectedCurrency?: any;
  selectedThresholdPageCurrency?: any;
}

type EditableField = "min" | "max" | "baseRate" | "clientRate" | "currency";

const ThresholdEditableTable: React.FC<Props> = ({
  resetKey,
  isInputPages,
  value,
  canAdd = true,
  onChange,
  onValidationError,
  setSelectedCurrency,
  selectedThresholdPageCurrency,
}) => {
  const [rows, setRows] = useState<ThresholdRow[]>([
    {
      min: "1",
      max: "",
      baseRate: "",
      clientRate: "",
      currency: "USD", // default
      disabled: false,
    },
  ]);

  const [isFirstSlabAdded, setIsFirstSlabAdded] = useState(false);

  const [errors, setErrors] = useState<string[]>([]);

  const isInitialized = React.useRef(false);

  useEffect(() => {
    setRows([
      {
        min: "1",
        max: "",
        baseRate: "",
        clientRate: "",
        currency: "USD", // default currency
        disabled: false,
      },
    ]);

    setIsFirstSlabAdded(false);
    setErrors([]);
    onValidationError([]);
    isInitialized.current = false;
  }, [resetKey]);

  useEffect(() => {
    if (!value) return;
    if (isInitialized.current) return;

    // If no existing slabs from API
    if (value.length === 0) {
      setRows([
        {
          min: "1",
          max: "",
          baseRate: "",
          clientRate: "",
          currency: "USD",
          disabled: false,
        },
      ]);
      setIsFirstSlabAdded(false);
      isInitialized.current = true;
      return;
    }

    // Take currency from first item (global currency)
    // const globalCurrency = value[0]?.currency || "USD";
    const globalCurrency = selectedThresholdPageCurrency || "USD";

    // If slabs exist
    const mappedRows: ThresholdRow[] = [
      ...(canAdd
        ? [
            {
              min: "",
              max: "",
              baseRate: "",
              clientRate: "",
              currency: globalCurrency,
              disabled: false,
            },
          ]
        : []),

      ...value.map((v) => ({
        min: String(v.min),
        max: String(v.max),
        baseRate: String(v.baseRate),
        clientRate: String(v.clientRate),
        currency: globalCurrency, // apply same currency to all
        disabled: true,
      })),
    ];

    setRows(mappedRows);
    setIsFirstSlabAdded(true);
    isInitialized.current = true;
  }, [value, canAdd]);

  useEffect(() => {
    if (!selectedThresholdPageCurrency) return;

    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        currency: selectedThresholdPageCurrency,
      })),
    );
  }, [selectedThresholdPageCurrency]);

  const handleChange = (index: number, field: EditableField, value: string) => {
    let updated = [...rows];

    // If currency is changed in first row → apply to all rows
    if (field === "currency" && index === 0) {
      updated = updated.map((row) => ({
        ...row,
        currency: value as "USD" | "INR",
      }));
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
    }

    setRows(updated);

    if (errors.length > 0) {
      setErrors([]);
      onValidationError([]);
    }
  };

  const hasInfiniteSlab = rows.some(
    (r, index) => index !== 0 && Number(r.max) === PAGE_SLAB_MAX_LIMIT,
  );

  const isOverlappingSlab = (
    newMin: number,
    newMax: number | null,
    existingRows: ThresholdRow[],
  ) => {
    return existingRows.some((row) => {
      // Use original values if row is in editing mode
      const existingMin = Number(
        row.isEditing && row.originalData ? row.originalData.min : row.min,
      );

      const existingMax = Number(
        row.isEditing && row.originalData ? row.originalData.max : row.max,
      );

      // Case 1: new max is EMPTY → treat as infinite
      if (newMax === null) {
        return newMin <= existingMax;
      }

      // Case 2: normal overlap check
      return newMin <= existingMax && newMax >= existingMin;
    });
  };

  const handleAddRow = () => {
    const currentRow = rows[0];
    const validationErrors: string[] = [];

    const min = Number(currentRow.min);
    const max =
      currentRow.max !== "" ? Number(currentRow.max) : PAGE_SLAB_MAX_LIMIT;

    const baseRate = Number(currentRow.baseRate);
    const clientRate = Number(currentRow.clientRate);

    if (!currentRow.min) {
      validationErrors.push("Minimum value is mandatory.");
    }

    if (!currentRow.baseRate) {
      validationErrors.push("Base Rate is mandatory.");
    }

    if (currentRow.baseRate !== "" && baseRate === 0) {
      validationErrors.push("Base Rate cannot be 0.");
    }

    if (!currentRow.clientRate) {
      validationErrors.push("Client Rate is mandatory.");
    }

    if (currentRow.clientRate !== "" && clientRate === 0) {
      validationErrors.push("Client Rate cannot be 0.");
    }

    if (currentRow.min !== "" && min === 0) {
      validationErrors.push("Minimum value cannot be 0.");
    }

    if (currentRow.max !== "" && max === 0) {
      validationErrors.push("Maximum value cannot be 0.");
    }

    if (currentRow.min && currentRow.max && min > Number(currentRow.max)) {
      validationErrors.push(
        "Minimum value cannot be greater than maximum value.",
      );
    }

    // Exclude Add row (index 0)
    const rowsToCheck = rows.filter((_, index) => index !== 0);

    if (currentRow.min && isOverlappingSlab(min, max, rowsToCheck)) {
      validationErrors.push(
        "The specified slab range overlaps with an existing configuration.",
      );
    }

    if (currentRow.max === "" && hasInfiniteSlab) {
      validationErrors.push("Only one slab can have no maximum value.");
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      onValidationError(validationErrors);
      return;
    }

    setErrors([]);
    onValidationError([]);

    setRows((prev) => {
      const inputRow = prev[0];
      const existingRows = prev.slice(1);

      const nextMin = getNextMinValue(prev);

      return [
        {
          min: nextMin,
          max: "",
          baseRate: "",
          clientRate: "",
          currency: inputRow.currency,
          disabled: false,
        },
        ...existingRows,
        {
          ...inputRow,
          max: inputRow.max === "" ? String(PAGE_SLAB_MAX_LIMIT) : inputRow.max,
          disabled: true,
        },
      ];
    });

    setIsFirstSlabAdded(true);
  };

  const getNextMinValue = (rows: ThresholdRow[]) => {
    const savedRows = rows.filter((r, index) => index !== 0);

    if (savedRows.length === 0) return "1";

    const highestMax = Math.max(
      ...savedRows.map((r) =>
        r.max === "" ? PAGE_SLAB_MAX_LIMIT : Number(r.max),
      ),
    );

    return highestMax + 1 > PAGE_SLAB_MAX_LIMIT ? "" : String(highestMax + 1);
  };

  useEffect(() => {
    if (!canAdd) return;

    let selectedCurrency = "";

    const payload: ThresholdPayload[] = rows
      .filter((r, index) => index !== 0)
      .map((r) => {
        const finalMax = r.max === "" ? PAGE_SLAB_MAX_LIMIT : Number(r.max);

        // capture currency from min === 1 row
        if (Number(r.min) === 1) {
          selectedCurrency = r.currency || "USD";
        }

        if (r.isEditing && r.originalData) {
          return {
            isInputPage: isInputPages === "Input" ? 1 : 0,
            min: Number(r.originalData.min),
            max:
              r.originalData.max === ""
                ? PAGE_SLAB_MAX_LIMIT
                : Number(r.originalData.max),
            baseRate: Number(r.originalData.baseRate),
            clientRate: Number(r.originalData.clientRate),
          };
        }

        return {
          isInputPage: isInputPages === "Input" ? 1 : 0,
          min: Number(r.min),
          max: finalMax,
          baseRate: Number(r.baseRate),
          clientRate: Number(r.clientRate),
        };
      });

    // send data WITHOUT currency
    onChange(payload);

    // send currency separately
    if (setSelectedCurrency && selectedCurrency) {
      setSelectedCurrency(selectedCurrency);
    }
  }, [rows, isInputPages, canAdd, onChange]);

  useEffect(() => {
    if (!canAdd) return;

    setRows((prev) => {
      if (prev.length <= 1) return prev;

      const nextMin = getNextMinValue(prev);

      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        min: nextMin,
      };

      return updated;
    });
  }, [
    rows
      .slice(1)
      .map((r) => r.max)
      .join(","),
  ]);

  const renderCell = (
    row: ThresholdRow,
    index: number,
    field: EditableField,
    allowDecimal = false,
  ) => {
    const disableMinField =
      field === "min" &&
      ((index === 0 && rows.length === 1) ||
        (row.isEditing && Number(row.min) === 1));

    if (row.disabled && !row.isEditing) {
      if (field === "max" && Number(row.max) === PAGE_SLAB_MAX_LIMIT) {
        return <span>Maximum</span>;
      }

      return <span>{row[field]}</span>;
    }

    return (
      <input
        type="text"
        value={row[field]}
        disabled={disableMinField}
        onChange={(e) => {
          const val = e.target.value;
          const regex = allowDecimal ? /^[0-9]*\.?[0-9]*$/ : /^[0-9]*$/;

          if (regex.test(val)) {
            if (
              (field === "max" || field === "min") &&
              val !== "" &&
              Number(val) > PAGE_SLAB_MAX_LIMIT
            ) {
              return;
            }

            handleChange(index, field, val);
          }
        }}
        className="form-control threshold-input"
      />
    );
  };

  const handleEditRow = (index: number) => {
    setRows((prev) =>
      prev.map((row, i) => {
        // If another row is being edited → restore it
        if (row.isEditing && i !== index && row.originalData) {
          return {
            ...row,
            ...row.originalData,
            isEditing: false,
            disabled: true,
          };
        }

        // Enable editing for clicked row
        if (i === index) {
          return {
            ...row,
            isEditing: true,
            disabled: false,
            originalData: {
              min: row.min,
              max: row.max,
              baseRate: row.baseRate,
              clientRate: row.clientRate,
              currency: row.currency,
            },
          };
        }

        return row;
      }),
    );
  };

  const handleCancelEdit = (index: number) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === index && row.originalData
          ? {
              ...row,
              ...row.originalData,
              isEditing: false,
              disabled: true,
            }
          : row,
      ),
    );
  };

  const handleSaveEdit = (index: number) => {
    const row = rows[index];
    const validationErrors: string[] = [];

    const min = Number(row.min);
    const max = row.max !== "" ? Number(row.max) : PAGE_SLAB_MAX_LIMIT;

    const baseRate = Number(row.baseRate);
    const clientRate = Number(row.clientRate);

    if (!row.min) validationErrors.push("Minimum value is mandatory.");

    // Base Rate
    if (!row.baseRate) validationErrors.push("Base Rate is mandatory.");
    if (row.baseRate !== "" && baseRate === 0) {
      validationErrors.push("Base Rate cannot be 0.");
    }

    // Client Rate
    if (!row.clientRate) validationErrors.push("Client Rate is mandatory.");
    if (row.clientRate !== "" && clientRate === 0) {
      validationErrors.push("Client Rate cannot be 0.");
    }

    if (min === 0) validationErrors.push("Minimum value cannot be 0.");

    if (row.max !== "" && max === 0) {
      validationErrors.push("Maximum value cannot be 0.");
    }

    if (row.min && row.max && min > Number(row.max)) {
      validationErrors.push(
        "Minimum value cannot be greater than maximum value.",
      );
    }

    // Overlap check
    const otherRows = rows.filter((_, i) => i !== index);

    if (isOverlappingSlab(min, max, otherRows)) {
      validationErrors.push(
        "The specified slab range overlaps with an existing configuration.",
      );
    }

    // Infinite slab check
    const hasInfiniteSlab = rows.some(
      (r, i) => i !== index && Number(r.max) === PAGE_SLAB_MAX_LIMIT,
    );

    if (row.max === "" && hasInfiniteSlab) {
      validationErrors.push("Only one slab can have no maximum value.");
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      onValidationError(validationErrors);
      return;
    }

    setErrors([]);
    onValidationError([]);

    const isBaseRow = row.min === "1";

    setRows((prev) => {
      let updated = prev.map((r, i) =>
        i === index
          ? {
              ...r,
              disabled: true,
              isEditing: false,
              originalData: undefined,
            }
          : r,
      );

      if (isBaseRow) {
        const selectedCurrency = row.currency;

        updated = updated.map((r) => ({
          ...r,
          currency: selectedCurrency,
        }));
      }

      return updated;
    });
  };

  return (
    <div className="table-responsive border-0 mb-2">
      <Table>
        <thead>
          <tr>
            {[
              "Min",
              "Max",
              "Base Rate",
              "Client Rate",
              ...(canAdd ? ["Action"] : [""]),
            ].map((h) => (
              <th
                key={h}
                style={{
                  border: "none",
                  padding: "6px",
                  backgroundColor: "#e0f7fa",
                  textAlign: "left",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td>{renderCell(row, index, "min")}</td>
              <td>{renderCell(row, index, "max")}</td>

              {/*  Base Rate with Currency */}
              <td>
                <CurrencyInputCompact
                  value={row.baseRate}
                  currency={row.currency || "USD"}
                  onValueChange={(val) => handleChange(index, "baseRate", val)}
                  onCurrencyChange={(val) =>
                    handleChange(index, "currency", val)
                  }
                  disabled={row.disabled && !row.isEditing}
                  // currencyDisabled={
                  //   row.min === "1"
                  //     ? isFirstSlabAdded
                  //       ? !row.isEditing
                  //       : false
                  //     : true
                  // }
                />
              </td>

              {/*  Client Rate */}
              <td>{renderCell(row, index, "clientRate", true)}</td>

              {/* Action Column */}
              <td style={{ padding: "6px", textAlign: "center" }}>
                {canAdd && !row.disabled && index === 0 && (
                  <button
                    onClick={handleAddRow}
                    className="btn btn-primary btn-sm d-flex align-items-center"
                  >
                    <span className="material-icons me-1">add</span>
                    Add
                  </button>
                )}

                {row.disabled && index !== 0 && !row.isEditing && (
                  <span
                    className="material-symbols-outlined"
                    style={{ cursor: "pointer", color: "#0092d5" }}
                    onClick={() => handleEditRow(index)}
                  >
                    edit
                  </span>
                )}

                {row.isEditing && (
                  <>
                    <span
                      className="material-symbols-outlined me-2"
                      style={{ cursor: "pointer", color: "#0092d5" }}
                      onClick={() => handleSaveEdit(index)}
                    >
                      check
                    </span>

                    <span
                      className="material-symbols-outlined"
                      style={{ cursor: "pointer", color: "red" }}
                      onClick={() => handleCancelEdit(index)}
                    >
                      close
                    </span>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <style>
        {`
        .threshold-input {
          height: 40px;
          padding: 2px 6px;
          font-size: 13px;
        }
      `}
      </style>
    </div>
  );
};

export default ThresholdEditableTable;
